// model/SavedState.js

/**
 * @fileoverview A class for managing saving and restoring the state of BMaps
 *
 * @typedef {import('BMapsModel').Workspace} Workspace
 * @typedef {import('BMapsModel').CaseData} CaseData
 * @typedef {import('BMapsSrc/style_manager').AtomGroupStyle} AtomGroupStyle
 * @typedef {import('BMapsSrc/DataConnection').default} DataConnection
 */

import _ from 'lodash';
import { App } from 'BMapsSrc/BMapsApp';
import { StyleManager } from 'BMapsSrc/style_manager';
import { Atom, CompoundHeritage, StarterCompounds } from 'BMapsModel';
import { getCameraPosition } from 'BMapsSrc/MainCanvas';
import { getJobState } from 'BMapsRedux/jobs/access';
import { getColorSchemeInfo, getBindingSiteDistance } from '../redux/prefs/access';
import {
    getTargetInfoMap, getTableState, getTargetGroups, getIndividualTargets,
} from '../redux/projectState/access';
import { asIndividualTarget } from '../redux/projectState/entry';
import { FoldingDataImportCase, MapCase, UserDataImportCase } from './MapCase';

const semver = require('semver');
/**
 * @description SavedState class
 */
export class SavedState {
    static get LatestVersion() { return '2.6.0'; }

    constructor(
        displayState,
        styleState,
        connections,
        heritage,
        structuresOfInterest,
        targetGroups=[],
        version=SavedState.LatestVersion
    ) {
        this.Version = version;
        this.Display = displayState;
        /** @type {AtomGroupStyle[]} */
        this.Style = styleState;
        /** @type {DataConnection[]} */
        this.Connections = connections;
        /** @type {ReturnType <typeof CompoundHeritage.captureAllHeritage>} */
        this.Heritage = heritage;
        this.StructuresOfInterest = structuresOfInterest;
        /** @type {{GroupId: string, DisplayName: string, Source: string,
         *          Members: string[]}[]} target-list groups (e.g. curated panels) */
        this.TargetGroups = targetGroups;
    }

    versionLessThan(input) {
        return semver.lt(this.Version, input);
    }

    versionAtLeast(input) {
        return semver.gte(this.Version, input);
    }

    supportsFeature(feature) {
        return feature && feature.minVersion
            && this.versionAtLeast(feature.minVersion);
    }

    /** @param {Workspace} workspace */
    static Capture(workspace) {
        const caseDataToIndexMap = SavedState.buildCaseDataMap(workspace);
        const connections = SavedState.CaptureDataConnections(workspace);
        const displayState = {
            Options: {
                BackgroundColor: getColorSchemeInfo().name,
                BindingSiteRadius: getBindingSiteDistance(),
            },
            State: workspace.displayState.saveState(),
            TreeState: workspace.systemTree.saveState(caseDataToIndexMap),
            TableState: captureTableState(workspace),
            JobState: captureJobState(workspace),
            Camera: getCameraPosition(),
        };

        const styleState = StyleManager.getGlobalStyleState();
        const heritage = CompoundHeritage.captureAllHeritage(workspace, caseDataToIndexMap);
        const strucuturesOfInterest = SavedState.captureStructuresOfInterest(workspace);
        const targetGroups = SavedState.captureTargetGroups();

        return new SavedState(
            displayState, styleState, connections, heritage, strucuturesOfInterest, targetGroups
        );
    }

    static Load(objIn) {
        const obj = (typeof objIn === 'string')
            ? JSON.parse(objIn)
            : { ...objIn }; // make sure we have a copy so version update doesn't affect original

        if (!semver.valid(obj.Version)) obj.Version = '0.1.0';
        return this.LatestVersionObjConverter(obj);
    }

    static LatestVersionObjConverter(obj) {
        let working = { ...obj };

        // Bail if attempting to import a more advanced major version.
        if (semver.major(working.Version) > semver.major(SavedState.LatestVersion)) {
            throw new Error('This saved session is from a newer version of BMaps and cannot be loaded.');
        }

        // Transform previous format into the latest format, step by step
        if (semver.lt(working.Version, '2.0.0')) {
            working = {
                Version: '2.0.0',
                Display: _.omit(working.Display, ['AtomGroupState']),
                Style: { ...working.Style },
                Connections: [
                    {
                        Mode: null,
                        LoadedCases: [
                            {
                                Protein: { ...working.Protein },
                                Compounds: [...working.Compounds],
                                AtomGroupState: { ...working.Display.AtomGroupState },
                                Styles: { AtomGroupsColorState: {} },
                            },
                        ],
                    },
                ],
            };
        }

        // Transform 2.0.0 to 2.0.1
        if (semver.lt(working.Version, '2.0.1')) {
            for (const conn of working.Connections) {
                for (const loadedCase of conn.LoadedCases) {
                    loadedCase.Styles.AtomGroupsMolStyleState = {};
                }
            }
        }

        // 2.0.2 introduced loadedCase.Protein.UserData.loadOptions
        // However this is only for user structures and we can't apply a default to old sessions.

        // Transform 2.0.1 to 2.1.0
        if (semver.lt(working.Version, '2.1.0')) {
            for (const conn of working.Connections) {
                for (const loadedCase of conn.LoadedCases) {
                    loadedCase.TargetInfo = { isTarget: true };
                }
            }
        }

        // Transform 2.1.0 to 2.2.0
        if (semver.lt(working.Version, '2.2.0')) {
            for (const conn of working.Connections) {
                for (const loadedCase of conn.LoadedCases) {
                    for (const [key, value] of Object.entries(
                        loadedCase.Styles.AtomGroupsColorState
                    )) {
                        loadedCase.Styles[key] = {
                            ...loadedCase.Styles[key],
                            color: value.color,
                            colorAllAtoms: typeof value.carbonsOnly === 'undefined'
                                ? false
                                : !value.carbonsOnly,
                        };
                    }
                    for (const [key, value] of Object.entries(
                        loadedCase.Styles.AtomGroupsMolStyleState
                    )) {
                        loadedCase.Styles[key] = {
                            ...loadedCase.Styles[key],
                            molStyle: value,
                        };
                    }
                    delete loadedCase.Styles.atomGroupsColorState;
                    delete loadedCase.Styles.atomGroupsMolStyleState;
                }
            }
        }

        if (semver.lt(working.Version, '2.3.0')) {
            working.Heritage = [];
        }

        if (semver.lt(working.Version, '2.4.0')) {
            working.Display.TableState = {};
            // mapCaseIndex was added to tree leaf items in 2.4.0 for multi-case support.
            // Build ordered queues per key so duplicate keys (same compound across multiple cases)
            // are assigned in load order rather than all mapping to the last case.
            const keyToCaseQueue = new Map();
            const addToQueue = (atomGroupKey, caseIdx) => {
                if (!keyToCaseQueue.has(atomGroupKey)) keyToCaseQueue.set(atomGroupKey, []);
                const queue = keyToCaseQueue.get(atomGroupKey);
                if (!queue.includes(caseIdx)) {
                    queue.push(caseIdx);
                }
            };
            let caseIndex = 0;
            for (const conn of working.Connections) {
                for (const loadedCase of conn.LoadedCases) {
                    for (const compound of loadedCase.Compounds || []) {
                        addToQueue(compound.name, caseIndex);
                    }
                    for (const entries of Object.values(loadedCase.AtomGroupState || {})) {
                        for (const { key } of entries) {
                            addToQueue(key, caseIndex);
                        }
                    }
                    caseIndex++;
                }
            }
            const singleCase = caseIndex === 1;
            const backfillCaseIndex = (items) => {
                for (const item of items) {
                    if (item.children) backfillCaseIndex(item.children);
                    else {
                        const queue = keyToCaseQueue.get(item.key);
                        if (queue && queue.length > 0) {
                            item.mapCaseIndex = queue.shift();
                        } else if (singleCase) {
                            item.mapCaseIndex = 0;
                        } else {
                            console.warn(`SavedState migration: could not map tree item key "${item.key}" to a case index`);
                        }
                    }
                }
            };
            const { TreeState } = working.Display;
            if (TreeState) {
                for (const tree of ['compoundsTree', 'fragmentsTree', 'proteinTree']) {
                    if (TreeState[tree]) backfillCaseIndex(TreeState[tree]);
                }
            }
        }

        if (semver.lt(working.Version, '2.5.0')) {
            working.StructuresOfInterest = [];
        }

        if (semver.lt(working.Version, '2.6.0')) {
            working.TargetGroups = [];
        }

        //
        // Add any additional transforms here, from 2.4.0 to the next...
        //

        // Finished transforming
        // Use the original version in the final result, not the latest.
        // This is so supportsFeature version checks can be used downstream
        return new SavedState(
            working.Display,
            working.Style,
            working.Connections,
            working.Heritage,
            working.StructuresOfInterest,
            working.TargetGroups,
            obj.Version
        );
    }

    static CaptureProtein(protein) {
        if (!protein || !(protein instanceof MapCase)) return null;

        const capturedProteinData = {
            CaseUri: protein.uri,
            DisplayName: protein.displayName,
            ScopedProperties: exportMapCaseScopedProperties(protein),
        };

        // CaptureProtein used for both structures loaded in bmaps and
        // for structures in project targets
        // So it may not have a caseData associated with it
        const caseData = App.getDataParents(protein).caseData;
        if (caseData) {
            const sampleCompoundInfo = caseData.sampleCompoundInfo;
            const sampleStatesToTrack = [StarterCompounds.Loaded, StarterCompounds.Dismissed];
            if (sampleStatesToTrack.includes(sampleCompoundInfo.availability)) {
                capturedProteinData.SampleCompoundStatus = sampleCompoundInfo.availability;
            }
        }
        if (protein instanceof UserDataImportCase || protein instanceof FoldingDataImportCase) {
            capturedProteinData.UserData = {
                data: protein.data,
                format: protein.data_format,
                name: protein.molecule_name,
                pdbid: protein.pdbID,
                sourceDesc: protein.sourceDesc,
                loadOptions: protein.loadOptions,
            };

            if (protein instanceof FoldingDataImportCase) {
                capturedProteinData.UserData.foldingData = protein.foldingData;
                capturedProteinData.UserData.resultProperties = protein.resultProperties;
            }
        }

        return capturedProteinData;
    }

    static CaptureCompound(compound, active, visible) {
        const obj = {
            name: compound.resSpec,
        };
        if (!compound.isLigand()) {
            obj.mol = compound.getMol2000();
            obj.energies = exportCompoundEnergies(compound);
            obj.solvation = exportCompoundSolvation(compound);
        }
        if (compound === active) obj.active = true;
        if (visible.includes(compound)) obj.visible = true;
        obj.scopedProperties = exportCompoundScopedProperties(compound);
        return obj;
    }

    /**
     * Return a map of CaseData to index for all loaded case data in the workspace.
     * This order will be the same as what is in the Connections array, so it can be
     * used to look up compounds and residues for specific cases when restoring.
     * @param {Workspace} workspace
     * @returns { Map<CaseData, number> }
     */
    static buildCaseDataMap(workspace) {
        // Use same procedure as CaptureDataConnections to list caseDataConnections
        const caseDataToIndexMap = new Map();
        for (const { caseDataCollection } of workspace.getDataConnections()) {
            const caseDatas = caseDataCollection.getAllCaseData().filter((cd) => !cd.isEmpty());
            for (const caseData of caseDatas.values()) {
                caseDataToIndexMap.set(caseData, caseDataToIndexMap.size);
            }
        }
        return caseDataToIndexMap;
    }

    /**
     * @param {Workspace} workspace
     * Collect all data connections with their connection type and case data (protein & compounds)
     */
    static CaptureDataConnections(workspace) {
        const Connections = [];
        const dataConnections = workspace.getDataConnections();

        for (const { connector, caseDataCollection } of dataConnections) {
            const caseDatas = caseDataCollection.getAllCaseData();
            const Mode = connector.getModeForSavedState();
            const LoadedCases = caseDatas
                .filter((caseData) => !caseData.isEmpty())
                .map((caseData) => SavedState.CaptureCaseData(caseData, workspace));
            Connections.push({ Mode, LoadedCases });
        }
        return Connections;
    }

    /**
     * Collect data for one caseData: protein, compounds, atomgroup state, and atomgroup style
     * @param {CaseData} caseData
     * @param {Workspace} workspace
     */
    static CaptureCaseData(caseData, workspace) {
        const Protein = this.CaptureProtein(caseData.mapCase);
        const Compounds = [];
        const activeCompound = workspace.getActiveCompound();
        const visibleCompounds = workspace.getVisibleCompounds(false);
        const compounds = caseData.getCompounds();
        const AtomGroupState = workspace.atomGroupState.saveState(caseData);
        const Styles = StyleManager.getAllAtomGroupStyles(caseData.allAtomGroups());

        compounds.forEach((cmpd) => {
            const compoundState = SavedState.CaptureCompound(
                cmpd, activeCompound, visibleCompounds
            );
            Compounds.push(compoundState);
        });
        const targetMap = getTargetInfoMap();

        if (caseData.mapCase) {
            // Defend against a bookkeeping error (loaded structure not having a target row)
            // resulting in a cyclic json serialization exception in targetInfo.
            const { mapCase, displayName, ...targetInfo } = asIndividualTarget(
                targetMap.get(caseData.mapCase),
            ) ?? {};
            return {
                Protein, Compounds, AtomGroupState, Styles, TargetInfo: targetInfo,
            };
        } else { // No protein case
            return {
                Compounds, AtomGroupState, Styles,
            };
        }
    }

    /**
     * Capture structures defined as structures of interest, but not loaded into BMaps
     * @param {Workspace} workspace
     * @returns {[{Protein: object, Compounds: object[], TargetInfo: object}]}
     */
    static captureStructuresOfInterest(workspace) {
        const loadedProteins = workspace.getLoadedProteins();
        const structuresOfInterest = getIndividualTargets().filter(
            (target) => !loadedProteins.includes(target.mapCase),
        );
        const state = [];
        for (const structure of structuresOfInterest) {
            const { mapCase, ...targetInfo } = structure;
            const Protein = this.CaptureProtein(mapCase);
            const TargetInfo = targetInfo;
            const Compounds = [];
            state.push({ Protein, Compounds, TargetInfo });
        }
        return state;
    }

    /**
     * Capture target-list groups (e.g. curated kinase panels) in a cycle-safe form: the
     * group's id/name/source plus its member uris (NOT the raw MapCase objects, which close a
     * CaseDataCollection cycle). Generic — works for any group, not just curated panels.
     * @returns {{GroupId: string, DisplayName: string, Source: string,
     *           Members: string[]}[]}
     */
    static captureTargetGroups() {
        return getTargetGroups().map((group) => ({
            GroupId: group.groupId,
            DisplayName: group.displayName,
            Source: group.source,
            Members: group.mapCases.map((mapCase) => mapCase.uri),
        }));
    }
}

/**
 * @description Return raw binding energy data for this compound.
 * {
 *      internalEnergies:    [ [summary], [detail] ],
 *      interactionEnergies: [ [summary], [detail] ]
 * }
 * @param {*} compound
 */
export function exportCompoundEnergies(compound) {
    return compound.originalEnergyData;
}

/**
 * @description Return raw solvation data for this compound.
 * @returns {
 *      totals: [ ddG_PL, ddG_LP, ddG_P, ddG_L],
 *      detail: {
 *          compound: [ [atomName, dGs, dGs_bound]... ],
 *          solute:   [ [uniqueID, dGs, dGs_bound]... ]
 *      },
 *      PSA: [ PSA, PSAbound, TSA, TSAbound,
 *              proteinPSA, proteinPSAbound,
 *              proteinTSA, proteinTSAbound ]
 * }
 * @param {*} compound
 */
export function exportCompoundSolvation(compound) {
    const data = compound.originalSolvData;

    if (data) {
        // The original data may have atom objects, so convert them
        // atom name or ids as appropriate.
        for (const e of data.detail.compound) {
            if (e[0] instanceof Atom) { e[0] = e[0].atom; } // atom name
        }

        for (const s of data.detail.solute) {
            if (s[0] instanceof Atom) {
                s[0] = s[0].uniqueID;
            }
        }
    }

    return data;
}

function exportCompoundScopedProperties(compound) {
    return compound?.scopedProperties?.forSerialization();
}

function exportMapCaseScopedProperties(mapCase) {
    return mapCase?.scopedProperties?.forSerialization();
}

function captureTableState(workspace) {
    const tableState = _.cloneDeep(getTableState());
    const availableCompounds = workspace.getLoadedCompounds().reduce((acc, cmpd) => {
        acc.add(cmpd.getUniqueStructId());
        return acc;
    }, new Set());

    // Scoring field is temporary and shouldn't be saved
    if (tableState.scoringTable) {
        delete tableState.scoringTable.scoring;
        const coralMap = tableState.scoringTable.chartState?.coralMap;
        if (coralMap) {
            // Don't save compounds that don't exist anymore.
            // Ideally the state should be cleaned up when structures/compounds are removed.
            let activeCompounds = coralMap.activeCompounds || [];
            activeCompounds = activeCompounds.filter((cmpd) => availableCompounds.has(cmpd));
            coralMap.activeCompounds = activeCompounds;
            // If there are no compounds to save, thresholds are meaningless
            if (activeCompounds.length === 0) {
                coralMap.propertyThresholds = {};
            }
        }
    }
    return simplifyValue(tableState);
}

function captureJobState(workspace) {
    const {
        localJobState,
        displayedCompoundJobs,
        displayedScreeningJobs,
    } = _.cloneDeep(getJobState());
    return simplifyValue({ localJobState, displayedCompoundJobs, displayedScreeningJobs });
}

/**
 * Recursively simplify an object to remove nulls and empty strings, arrays, and objects.
 * @param {any} value
 * @returns {any}
 */
function simplifyValue(value) {
    if (!value && value !== 0) return null;

    if (typeof value === 'object') {
        if (Object.keys(value).length === 0) return null;
        if (Array.isArray(value)) {
            const newArr = value.map((v) => simplifyValue(v)).filter((v) => v !== null);
            return newArr.length > 0 ? newArr : null;
        } else {
            const newObj = Object.entries(value).reduce((acc, [key, val]) => {
                const simplifiedVal = simplifyValue(val);
                if (simplifiedVal !== null) {
                    acc[key] = simplifiedVal;
                }
                return acc;
            }, {});
            return Object.keys(newObj).length > 0 ? newObj : null;
        }
    }
    return value;
}

SavedState.Features = {
    EnergyDetail1: { minVersion: '1.0.0' },
    TreeStructure1: { minVersion: '1.1.0' },
};
